// src/communaute/Notifications.jsx
//
// Liste paginée des notifications : filtre non lues, tout lire,
// supprimer une / toutes, clic → ouvre la cible (via onOuvrir).

import { useCallback, useEffect, useState } from 'react';
import {
    Bell,
    BellOff,
    CalendarDays,
    CheckCheck,
    Crown,
    FileText,
    Filter,
    FilterX,
    Heart,
    MessageCircle,
    MessageSquare,
    Trash2,
    UserPlus,
    Users,
} from 'lucide-react';

import {
    getNotifications,
    marquerNotifLue,
    supprimerNotif,
    toutMarquerLu,
    toutSupprimerNotifs,
} from './api';
import { tempsRelatif } from './normalize';
import { Avatar, Chargement, Pastille, Spinner, Vide, confirmer, toast, useScrollInfini } from './ui';

// Même correspondance que NotificationRouter.dart
export const STYLE_NOTIF = {
    evenement: { icone: CalendarDays, couleur: '#7C3AED' },
    message: { icone: MessageSquare, couleur: '#3B82F6' },
    follow: { icone: UserPlus, couleur: '#10B981' },
    post: { icone: FileText, couleur: '#9B59B6' },
    commentaire: { icone: MessageCircle, couleur: '#9B59B6' },
    like: { icone: Heart, couleur: '#EF4444' },
    groupe: { icone: Users, couleur: '#6366F1' },
    abonnement: { icone: Crown, couleur: '#F59E0B' },
};
const STYLE_DEFAUT = { icone: Bell, couleur: '#6b7280' };

export default function Notifications({ onOuvrir, onNonLuesChange }) {
    const [filtre, setFiltre] = useState('tous');
    const [notifs, setNotifs] = useState([]);
    const [chargement, setChargement] = useState(true);
    const [chargementPlus, setChargementPlus] = useState(false);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [nonLues, setNonLues] = useState(0);

    const majNonLues = (n) => {
        setNonLues(n);
        onNonLuesChange?.(n);
    };

    const charger = useCallback(async () => {
        setChargement(true);
        const r = await getNotifications(1, filtre);
        setNotifs(r.items);
        setPage(r.page);
        setLastPage(r.lastPage);
        setNonLues(r.nonLues);
        onNonLuesChange?.(r.nonLues);
        setChargement(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtre]);

    useEffect(() => {
        charger();
    }, [charger]);

    const suite = async () => {
        if (chargementPlus || page >= lastPage) return;
        setChargementPlus(true);
        const r = await getNotifications(page + 1, filtre);
        setNotifs((l) => [...l, ...r.items]);
        setPage(r.page);
        setLastPage(r.lastPage);
        setChargementPlus(false);
    };
    const sentinelle = useScrollInfini(suite, !chargement && page < lastPage);

    const ouvrir = (n) => {
        if (!n.lu) {
            setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, lu: true } : x)));
            majNonLues(Math.max(0, nonLues - 1));
            marquerNotifLue(n.id);
        }
        onOuvrir?.(n);
    };

    const supprimer = async (e, n) => {
        e.stopPropagation();
        setNotifs((l) => l.filter((x) => x.id !== n.id));
        if (!n.lu) majNonLues(Math.max(0, nonLues - 1));
        await supprimerNotif(n.id);
    };

    const toutLire = async () => {
        if (await toutMarquerLu()) {
            setNotifs((l) => l.map((x) => ({ ...x, lu: true })));
            majNonLues(0);
            toast('Tout marqué comme lu', true);
        }
    };

    const toutSupprimer = async () => {
        const ok = await confirmer({
            titre: 'Tout supprimer ?',
            message: 'Toutes vos notifications seront définitivement supprimées.',
            bouton: 'Supprimer',
        });
        if (!ok) return;
        if (await toutSupprimerNotifs()) {
            setNotifs([]);
            majNonLues(0);
        }
    };

    return (
        <div className="mx-auto max-w-2xl">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <p className="mr-auto text-sm text-gray-500">
                    {nonLues > 0 ? (
                        <>
                            Non lues <Pastille n={nonLues} />
                        </>
                    ) : (
                        'Tout est lu'
                    )}
                </p>
                <button
                    onClick={() => setFiltre((f) => (f === 'tous' ? 'non_lues' : 'tous'))}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100"
                >
                    {filtre === 'tous' ? <Filter size={15} /> : <FilterX size={15} />}
                    {filtre === 'tous' ? 'Non lues uniquement' : 'Toutes'}
                </button>
                <button
                    onClick={toutLire}
                    disabled={!nonLues}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-color1 px-4 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:brightness-100"
                >
                    <CheckCheck size={15} /> Tout marquer comme lu
                </button>
                <button
                    onClick={toutSupprimer}
                    disabled={!notifs.length}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                >
                    <Trash2 size={15} /> Tout supprimer
                </button>
            </div>

            {chargement ? (
                <Chargement />
            ) : notifs.length === 0 ? (
                <Vide icone={BellOff} titre={filtre === 'non_lues' ? 'Aucune notification non lue' : 'Aucune notification'} />
            ) : (
                <div className="overflow-hidden rounded-2xl border border-gray-200">
                    {notifs.map((n) => {
                        const { icone: Icone, couleur } = STYLE_NOTIF[n.type] || STYLE_DEFAUT;
                        return (
                            <div
                                key={n.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => ouvrir(n)}
                                onKeyDown={(e) => e.key === 'Enter' && ouvrir(n)}
                                className={`group flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3.5 last:border-0 hover:bg-gray-100 ${n.lu ? '' : 'bg-[#7C3AED]/[0.07]'
                                    }`}
                            >
                                <div className="relative">
                                    {n.sender?.photo ? (
                                        <>
                                            <Avatar src={n.sender.photo} taille={44} />
                                            <span
                                                className="absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white p-[3px]"
                                                style={{ backgroundColor: couleur }}
                                            >
                                                <Icone size={9} className="text-white" />
                                            </span>
                                        </>
                                    ) : (
                                        <span
                                            className="flex h-11 w-11 items-center justify-center rounded-full"
                                            style={{ backgroundColor: `${couleur}26`, color: couleur }}
                                        >
                                            <Icone size={21} />
                                        </span>
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline gap-2">
                                        <p className={`truncate text-[14.5px] text-gray-900 ${n.lu ? 'font-semibold' : 'font-bold'}`}>{n.titre}</p>
                                        <span className={`ml-auto shrink-0 text-[11px] ${n.lu ? 'text-gray-400' : 'font-bold text-[#9B59B6]'}`}>
                                            {n.dateTexte || tempsRelatif(n.date)}
                                        </span>
                                    </div>
                                    <p className={`mt-0.5 line-clamp-2 text-[13px] leading-snug ${n.lu ? 'text-gray-500' : 'text-gray-600'}`}>
                                        {n.contenu}
                                    </p>
                                </div>

                                <div className="flex flex-col items-center gap-2 pt-1">
                                    {!n.lu && <span className="h-2 w-2 rounded-full bg-[#7C3AED]" />}
                                    <button
                                        onClick={(e) => supprimer(e, n)}
                                        className="rounded-full p-1 text-gray-300 opacity-0 hover:bg-gray-100 hover:text-red-600 group-hover:opacity-100"
                                        title="Supprimer"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={sentinelle} />
                    {chargementPlus && (
                        <div className="flex justify-center py-3">
                            <Spinner taille={20} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}